import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { User, Yard } from '../../types'
import { getUserYardAssignments, updateUserYardAssignments } from '../../services/yardAssignments'
import { getYards } from '../../services/api'

interface YardAssignmentDialogProps {
  open: boolean
  onClose: () => void
  user: User
  onSuccess?: () => void
}

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`

const YardList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 4px;
  padding: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
`

const YardAssignmentDialog: React.FC<YardAssignmentDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [yardPermissions, setYardPermissions] = useState<'all' | 'assigned'>('assigned')
  const [selectedYards, setSelectedYards] = useState<string[]>([])
  const [availableYards, setAvailableYards] = useState<Yard[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && user) {
      loadData()
    }
  }, [open, user])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      // Load available yards
      const yardsRes = await getYards()
      setAvailableYards(yardsRes.data || [])

      // Load current assignments
      const assignmentsRes = await getUserYardAssignments(user.id)
      setYardPermissions(assignmentsRes.yardPermissions || 'assigned')
      setSelectedYards(assignmentsRes.assignedYards || [])
    } catch (err: any) {
      console.error('Error loading yard assignments:', err)
      setError(err.response?.data?.error || t('users.yardAccess.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleYardToggle = (yardId: string) => {
    setSelectedYards(prev =>
      prev.includes(yardId)
        ? prev.filter(id => id !== yardId)
        : [...prev, yardId]
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await updateUserYardAssignments(user.id, {
        yardPermissions,
        assignedYards: yardPermissions === 'all' ? [] : selectedYards,
      })
      
      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (err: any) {
      console.error('Error updating yard assignments:', err)
      setError(err.response?.data?.error || t('users.yardAccess.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('users.yardAccess.title', { name: user.name })}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <FormSection>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('users.yardAccess.level')}</FormLabel>
                <RadioGroup
                  value={yardPermissions}
                  onChange={e => setYardPermissions(e.target.value as 'all' | 'assigned')}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label={
                      <div>
                        <Typography variant="body2" fontWeight="bold">
                          {t('users.yardAccess.allYards')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('users.yardAccess.allYardsHint')}
                        </Typography>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="assigned"
                    control={<Radio />}
                    label={
                      <div>
                        <Typography variant="body2" fontWeight="bold">
                          {t('users.yardAccess.assignedOnly')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('users.yardAccess.assignedOnlyHint')}
                        </Typography>
                      </div>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </FormSection>

            {yardPermissions === 'assigned' && (
              <FormSection>
                <FormLabel component="legend">
                  {t('users.yardAccess.selectYards', { count: selectedYards.length })}
                </FormLabel>
                <YardList>
                  {availableYards.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('users.yardAccess.noYards')}
                    </Typography>
                  ) : (
                    <FormGroup>
                      {availableYards.map(yard => (
                        <FormControlLabel
                          key={yard.id}
                          control={
                            <Checkbox
                              checked={selectedYards.includes(String(yard.id || yard._id))}
                              onChange={() => handleYardToggle(String(yard.id || yard._id))}
                            />
                          }
                          label={
                            <div>
                              <Typography variant="body2">{yard.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {yard.address}
                              </Typography>
                            </div>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                </YardList>
              </FormSection>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('button.cancel.text')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={24} /> : t('button.save.text')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default YardAssignmentDialog
